<template>
  <div class="container">
    <div
      class="banner-content row"
      :class="{ shadow: withShadow }"
      :style="{ background: backgroundGradient }"
    >
      <div class="col-md-6">
        <prismic-rich-text
          v-if="title"
          class="title"
          :field="title"
          :style="{ color: textColor }"
        ></prismic-rich-text>

        <prismic-rich-text
          v-if="subtitle"
          class="subtitle"
          :field="subtitle"
          :style="{ color: textColor }"
        ></prismic-rich-text>
      </div>
      <div class="col-md-6 buttons-container">
        <Button
          v-if="showFirstButton"
          :blank="!button1IsInternal"
          :gradient="button1BackgroundColor"
          :font-color="button1TextColor"
          :to="button1Link"
          v-bind="buttonSharedProps"
          @click="button1Click"
        >
          {{ button1Text }}</Button
        >
        <Button
          v-if="showSecondButton"
          :blank="!button2IsInternal"
          :gradient="button2BackgroundColor"
          :font-color="button2TextColor"
          :to="button2Link"
          v-bind="buttonSharedProps"
          @click="button2Click"
        >
          {{ button2Text }}</Button
        >
      </div>
    </div>
  </div>
</template>

<script>
import Button from 'UI/Button';
import { mapGetters } from 'vuex';

export default {
  name: 'Banner',
  components: {
    Button
  },
  props: {
    slice: {
      type: Object,
      required: true
    }
  },
  data: () => {
    return {
      title: '',
      subtitle: '',
      textColor: '',
      withShadow: false,
      backgroundColor1: '',
      backgroundColor2: '',
      button1Text: '',
      button1TextColor: '',
      button1BackgroundColor: '',
      button1Path: '',
      button1ScrollToId: false,
      button1OpenModal: false,
      button1IsInternal: false,
      button2Text: '',
      button2TextColor: '',
      button2BackgroundColor: '',
      button2Path: '',
      button2ScrollToId: false,
      button2OpenModal: false,
      button2IsInternal: false
    };
  },
  computed: {
    ...mapGetters('experience', ['getExperienceColor']),
    backgroundGradient() {
      return (
        'transparent linear-gradient(85deg, ' +
        this.backgroundColor1 +
        ' 0%, ' +
        this.backgroundColor2 +
        ' 100%) 0% 0% no-repeat padding-box'
      );
    },
    showFirstButton() {
      return this.button1Text && this.button1Path;
    },
    showSecondButton() {
      return this.button2Text && this.button2Path;
    },
    buttonSharedProps() {
      const bothButtons = this.showFirstButton && this.showSecondButton;
      const props = { 'with-gradient': true };
      if (bothButtons) {
        props.width = 'full';
        props.class = 'w-100';
      } else {
        props.size = 'long';
      }
      return props;
    },
    button1Link() {
      return !this.button1ScrollToId && !this.button1OpenModal
        ? this.button1IsInternal
          ? this.button1Path.startsWith('javascript:')
            ? this.button1Path
            : this.$trailingSlash(this.localePath(this.button1Path))
          : this.button1Path
        : '';
    },
    button2Link() {
      return !this.button2ScrollToId && !this.button2OpenModal
        ? this.button2IsInternal
          ? this.button2Path.startsWith('javascript:')
            ? this.button2Path
            : this.$trailingSlash(this.localePath(this.button2Path))
          : this.button2Path
        : '';
    }
  },
  created() {
    const { primary } = this.slice;

    this.title = primary.title1[0]?.text ? primary.title1 : '';
    this.subtitle = primary.subtitle[0]?.text ? primary.subtitle : '';

    this.textColor = primary.text_color ? primary.text_color : '#fff';
    this.backgroundColor1 = primary.background_color1
      ? primary.background_color1
      : this.getExperienceColor;
    this.backgroundColor2 = primary.background_color2
      ? primary.background_color2
      : this.getExperienceColor;

    this.button1Text = primary.button1_text;
    this.button1TextColor = primary.button1_text_color
      ? primary.button1_text_color
      : this.getExperienceColor;
    this.button1BackgroundColor = primary.button1_background_color
      ? primary.button1_background_color
      : '#fff';
    this.button1Path = primary.button1_path;
    this.button1ScrollToId = primary.button1_scroll_to_id;
    this.button1OpenModal = primary.button1_open_modal;
    this.button1IsInternal = primary.button1_is_internal_link;

    this.withShadow = primary.with_shadow;
    this.button2Text = primary.button2_text;
    this.button2TextColor = primary.button2_text_color ? primary.button2_text_color : '#000';
    this.button2BackgroundColor = primary.button2_background_color
      ? primary.button2_background_color
      : '#000';
    this.button2Path = primary.button2_path;
    this.button2ScrollToId = primary.button2_scroll_to_id;
    this.button2OpenModal = primary.button2_open_modal;
    this.button2IsInternal = primary.button2_is_internal_link;
  },
  methods: {
    button1Click() {
      this.$dataLayerPush('banner_slice_button_click', {
        bannerSlice: {
          clickUrl: this.button1Path
        }
      });
      if (this.button1ScrollToId) {
        this.scrollToElement(this.button1Path);
      } else if (this.button1OpenModal) {
        this.$bvModal.show(this.button1Path);
      }
    },
    button2Click() {
      this.$dataLayerPush('banner_slice_button_click', {
        bannerSlice: {
          clickUrl: this.button2Path
        }
      });
      if (this.button2ScrollToId) {
        this.scrollToElement(this.button2Path);
      } else if (this.button2OpenModal) {
        this.$bvModal.show(this.button2Path);
      }
    },
    scrollToElement(element) {
      const options = {
        offset: -100
      };
      this.$scrollTo(element, 500, options);
    }
  }
};
</script>

<style lang="scss" scoped>
.banner-content {
  border-radius: 15px;
  padding: 45px 60px;
  &.shadow {
    box-shadow: 0px 9px 25px #00000029;
  }
  @include md {
    padding: 20px 30px;
  }
  .buttons-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    span {
      padding: 0 5px;
    }
    @include md {
      flex-direction: column;
      span {
        padding: 10px 5px;
      }
    }
  }
}
</style>
<style lang="scss">
.banner-content {
  .title {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-size: 39px;
      @include sm {
        font-size: 27px;
      }
    }
  }
  .subtitle {
    p,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-size: 15px;
    }
  }
}
</style>
